import { FC } from 'react'

import {
  ArrowRightIcon,
  ChevronRightIcon,
  LockFullIcon,
  StarFullIcon
} from '@inno-ui/icons'
import classNames from 'classnames'
import { motion } from 'motion/react'
import { useTranslation } from 'react-i18next'
import { AppButtonWrapper } from 'shared/components/MainLayout/components/AppButton/components'
import { transformAppConfigToAppData } from 'shared/components/MainLayout/components/AppButton/utils/appDataTransform'

import {
  SPOTLIGHT_CARD_STAGGER,
  VIEW_TRANSITION
} from '../../LaunchPad.animations'
import styles from './SpotlightHero.module.scss'
import { SpotlightHeroProps } from './SpotlightHero.types'

export const SpotlightHero: FC<SpotlightHeroProps> = ({
  apps,
  variant,
  isAppLocked,
  onAppClick,
  onShowAllApps
}) => {
  const isList = variant === 'dark'
  const { t } = useTranslation('translation', {
    keyPrefix: 'launchpad.spotlight'
  })

  return (
    <section
      className={classNames(styles.spotlightHero, styles[variant])}
      data-tour='launchpad-spotlight'
    >
      <div className={styles.header}>
        <h2 className={styles.title}>
          <StarFullIcon className={styles.headerIcon} />
          {t('title')}
        </h2>
        <p className={styles.subtitle}>{t('subtitle')}</p>
      </div>
      <div className={styles.cards}>
        {apps.map((app, index) => {
          const isLocked = isAppLocked(app)
          return (
            <AppButtonWrapper
              key={app.id}
              app={transformAppConfigToAppData(app)}
              buttonType='launchpad'
              isLocked={isLocked}
              dataTour={`launchpad-spotlight-${app.id}`}
              handleOpenWindow={() => onAppClick(app)}
            >
              <motion.button
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  ...VIEW_TRANSITION,
                  delay: 0.08 + index * SPOTLIGHT_CARD_STAGGER
                }}
                whileHover={isLocked || isList ? undefined : { y: -4 }}
                whileTap={isLocked ? undefined : { scale: 0.98 }}
                type='button'
                className={styles.card}
                disabled={isLocked}
                onClick={() => onAppClick(app)}
              >
                <span className={styles.cardIconTile}>
                  <img
                    className={styles.cardIcon}
                    src={app.appIcon ?? app.icon}
                    alt=''
                  />
                </span>
                <span className={styles.cardName}>{app.label}</span>
                {app.description && (
                  <span className={styles.cardDescription}>
                    {app.description}
                  </span>
                )}
                {isLocked ? (
                  <LockFullIcon className={styles.lockIcon} />
                ) : (
                  isList && <ChevronRightIcon className={styles.chevron} />
                )}
              </motion.button>
            </AppButtonWrapper>
          )
        })}
      </div>
      <button
        type='button'
        className={styles.showAllButton}
        onClick={e => {
          e.stopPropagation()
          onShowAllApps()
        }}
      >
        {t('show_all_apps')}
        <ArrowRightIcon />
      </button>
    </section>
  )
}
